import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { getApiBaseUrl } from "../../utils/environment";
import { background, borderRadius, display, flexDirection, fontSize, fontWeight, marginTop, overflow, padding, textAlign } from 'styled-system';
import { Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';


const baseUrl = getApiBaseUrl();

const PatientLogin = () => {
    const [token, setToken] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [loading, setLoading] = useState(true);
    const [lastName, setLastName] = useState('');
    const [dob, setDob] = useState('');
    const [error, setError] = useState('');

    const [providerLogo, setProviderLogo] = useState('');
    const [providerName, setProviderName] = useState('');
    const [followUpMessage, setFollowUpMessage] = useState('');
    const [created, setCreated] = useState('');


    const lastNameRef = useRef(null);

    useEffect(() => {
        if (!loading && isTokenValid && !followUpMessage && lastNameRef.current) {
            lastNameRef.current.focus();
        }
    }, [loading, isTokenValid, followUpMessage]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('token');

        if (!tokenFromUrl) {
            setError('Token is missing in the URL');
            setLoading(false);
            return;
        }

        setToken(tokenFromUrl);

        fetch(`${baseUrl}/api/v1/patient-portal/verification/`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: tokenFromUrl }),
        })
            .then(res => {
                if (!res.ok) throw new Error('Invalid or expired token');
                return res.json();
            })
            .then(data => {
                setIsTokenValid(true);
                setProviderLogo(data.provider?.logo_url || '');
                setProviderName(data.provider?.name || '');

                if (Array.isArray(data?.messages)) {
                    setFollowUpMessage(data.messages);
                    setCreated(data.created || '');
                }
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const handleVerify = () => {
        if (!lastName || !dob) {
            setError('Please enter both Last Name and Date of Birth');
            return;
        }

        setError('');
        setLoading(true);

        fetch(`${baseUrl}/api/v1/patient-portal/verification/`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                token,
                last_name: lastName,
                dob: moment(dob).format('YYYY-MM-DD'),
                "with_dob_check": true,
            }),
        })
            .then(res => {
                if (!res.ok && res.status == 429) throw new Error(`Too many failed attempts. Please try again later.`);
                if (!res.ok) throw new Error(`Sorry, we couldn’t find a record using this last name & date of birth. Please check and try again.`);
                return res.json();
            })
            .then(data => {
                setFollowUpMessage(Array.isArray(data?.messages) ? data.messages : '');
                setProviderLogo(data.provider?.logo_url || providerLogo);
                setProviderName(data.provider?.name || providerName);
                setCreated(data.created || '');
            })
            .catch(err => {
                setError(err.message)
            })
            .finally(() => setLoading(false));
    };

    const handleCall = (label, number) => {
        window.location.href = `tel:${number}`;
    };

    if (loading) return <div style={styles.loadingContainer}>
        <Spinner animation="border" role="status" size="sm" />
    </div>;


    const CustomInput = React.forwardRef(({ value, onClick, onChange, placeholder }, ref) => (
        <input
            type="text"
            value={value}
            onClick={onClick}
            onChange={onChange}
            placeholder={placeholder}
            style={{ ...styles.input, width: '100%' }}
            ref={ref}
        />
    ));

    return (
        <div style={styles.container}>
            {(providerLogo || providerName) && (
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                    {providerLogo && (
                        <img src={providerLogo} alt="Provider Logo" style={{ maxWidth: '150px', marginBottom: '0.5rem', marginLeft: 'auto', marginRight: 'auto' }} />
                    )}
                    {providerName && !followUpMessage && <div style={{ fontWeight: 600, fontSize: '22px' }}>Please login to view your messages from {providerName}</div>}
                </div>
            )}


            {!isTokenValid ? (
                <>
                    <div style={styles.invalid}>{error}</div>
                </>
            ) : Array.isArray(followUpMessage) ? (
                <div>
                    {
                        followUpMessage?.map((itm, idx) => (

                            <div >
                                <div key={idx} style={styles.bubble}>{itm.message}</div>
                                {itm.created && (
                                    <div style={styles.timestamp}>
                                        {moment(itm.created).format('YYYY/MM/DD hh:mm a')}
                                    </div>
                                )}

                            </div>
                        ))
                    }


                </div>

            ) : (
                <div>
                    <div style={styles.inputGroup}>
                        <label style={{ fontWeight: 600 }}>Last name</label>
                        <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            style={styles.input}
                            ref={lastNameRef}
                            placeholder='Last Name'
                        />
                    </div>
                    <div style={styles.inputGroup}>
                        <label style={{
                            fontWeight: 600, appearance: 'none', 
                            WebkitAppearance: 'none',
                        }}>Date of birth</label>
                        <DatePicker
                            selected={dob}
                            onChange={(date) => setDob(date)}
                            dateFormat="MM-dd-yyyy"
                            placeholderText="MM/DD/YY"
                            className="custom-datepicker"
                            maxDate={new Date()}
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            popperPlacement="bottom"
                            popperModifiers={[
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 10],
                                    },
                                },

                            ]}
                            customInput={<CustomInput />}
                        />
                    </div>
                    <button onClick={handleVerify} style={{
                        ...styles.verifyButton,
                        backgroundColor: lastName && dob ? '#000000' : '#999999',
                    }}>
                        Verify Information
                    </button>
                    {error && <div style={styles.error}>{error}</div>}

                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        padding: '33px 50px',
        maxWidth: '500px',
        margin: 'auto',
        fontFamily: 'Arial, sans-serif',
        display: 'flex',
        height: '100%',
        flexDirection: 'column'
    },
    inputGroup: {
        marginBottom: '1rem',
        with: '100%',
        display: 'flex',
        flexDirection: 'column'

    },
    input: {
        width: '100%',
        padding: '8px',
        fontSize: '16px',
        border: '1px solid #D2D3D7',
        borderRadius: '4px'
    },
    verifyButton: {
        width: '100%',
        padding: '10px',
        backgroundColor: '#999999',
        color: '#fff',
        border: 'none',
        fontSize: '16px',
        borderRadius: '4px',
    },
    error: {
        color: '#BC0000',
        background: '#FFEEEE',
        marginBottom: '1rem',
        fontWeight: 600,
        fontSize: '16px',
        borderRadius: '4px',
        padding: '10px 15px',
        marginTop: '19px',
    },
    invalid: {
        color: '#BC0000',
        background: '#FFEEEE',
        marginBottom: '1rem',
        fontWeight: 600,
        fontSize: '16px',
        borderRadius: '4px',
        padding: '10px 15px',
        marginTop: '19px',
        textAlign: 'center'
    },
    bubble: {
        backgroundColor: '#00AEEF',
        color: 'white',
        padding: '6px 22px',
        borderRadius: '30px',
        marginBottom: '8px',
        fontSize: '16px',
        fontWeight: 400,
        maxWidth: '100%',
        maxHeight: '200px',
        wordBreak: 'break-all',
        overflowY: 'auto'
    },
    timestamp: {
        fontSize: '10px',
        fontWeight: 400,
        marginBottom: '1rem',
        textAlign: 'end'
    },
    buttonGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        marginTop: 'auto'
    },
    button: {
        padding: '12px',
        color: '#fff',
        border: 'none',
        fontSize: '16px',
        borderRadius: '30px',
        fontWeight: 600
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '20px',
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif',
    },
};

export default PatientLogin;
